import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Grid } from '@mui/system';
import { CustomContainer } from 'components/CustomContainer';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import type { ScalarSpreadsheetProps } from 'components/ScalarSpreadsheet/ScalarSpreadsheet';
import {
  DEFAULT_XLSX_PREVIEW_COLUMN_WIDTH_PX,
  DEFAULT_XLSX_ROW_HEIGHT_PX,
  getXlsxColumnWidthPx,
  getXlsxRowHeightPx,
  INTERNAL_REFERENCE_XLSX_ROW_HEIGHT_PX,
} from 'components/SpreadsheetNotes/utilities/xlsxLayout.utils';
import { SpreadsheetNoteData, SpreadsheetNoteItemProps, SpreadsheetNoteXlsxImage } from '../types';
import { XLSX_PREVIEW_VIRTUAL_ROW_HEIGHT } from './xlsxPreviewVirtualRowHeight';

const MAX_CONTAINER_WIDTH = 4000;
const XLSX_PREVIEW_VIRTUALIZATION_ROW_THRESHOLD = 250;
const XLSX_PREVIEW_GRID_PADDING_X_PX = 8;
const XLSX_PREVIEW_GRID_PADDING_Y_PX = 24;
const XLSX_PREVIEW_COLUMN_LEGEND_HEIGHT_PX = 25;
const XLSX_PREVIEW_ROW_LEGEND_WIDTH_PX = 32;
const XLSX_PREVIEW_IMAGE_FALLBACK_SIZE_PX = 96;
const XLSX_PREVIEW_IMAGE_MIN_SIZE_PX = 8;

type XlsxImageOverlayProps = {
  columnOffset: number;
  columnWidths: number[];
  images: SpreadsheetNoteXlsxImage[];
  rowHeights: number[];
  rowOffset: number;
};

function getXlsxPreviewRowCount(note: SpreadsheetNoteItemProps['note']): number {
  const rowData = note.initialObj?.content?.row_data;
  if (rowData && typeof rowData === 'object') {
    return Object.keys(rowData).length;
  }
  return 0;
}

const getAccumulatedSizeBeforeIndex = (sizes: number[], index: number, fallbackSize: number) => {
  let total = 0;

  for (let currentIndex = 0; currentIndex < index; currentIndex += 1) {
    total += sizes[currentIndex] ?? fallbackSize;
  }

  return total;
};

const getImageMarkerLayout = (
  marker: SpreadsheetNoteXlsxImage['from'],
  columnWidths: number[],
  rowHeights: number[],
  columnOffset: number,
  rowOffset: number
) => ({
  left:
    XLSX_PREVIEW_GRID_PADDING_X_PX +
    XLSX_PREVIEW_ROW_LEGEND_WIDTH_PX +
    getAccumulatedSizeBeforeIndex(columnWidths, marker.column + columnOffset, DEFAULT_XLSX_PREVIEW_COLUMN_WIDTH_PX) +
    marker.columnOffsetPx,
  top:
    XLSX_PREVIEW_GRID_PADDING_Y_PX +
    XLSX_PREVIEW_COLUMN_LEGEND_HEIGHT_PX +
    getAccumulatedSizeBeforeIndex(rowHeights, marker.row + rowOffset, DEFAULT_XLSX_ROW_HEIGHT_PX) +
    marker.rowOffsetPx,
});

const getImageLayout = (
  image: SpreadsheetNoteXlsxImage,
  columnWidths: number[],
  rowHeights: number[],
  columnOffset: number,
  rowOffset: number
) => {
  const fromLayout = getImageMarkerLayout(image.from, columnWidths, rowHeights, columnOffset, rowOffset);
  const toLayout = image.to
    ? getImageMarkerLayout(image.to, columnWidths, rowHeights, columnOffset, rowOffset)
    : undefined;

  const width = Math.max(
    XLSX_PREVIEW_IMAGE_MIN_SIZE_PX,
    toLayout ? toLayout.left - fromLayout.left : (image.size?.widthPx ?? XLSX_PREVIEW_IMAGE_FALLBACK_SIZE_PX)
  );
  const height = Math.max(
    XLSX_PREVIEW_IMAGE_MIN_SIZE_PX,
    toLayout ? toLayout.top - fromLayout.top : (image.size?.heightPx ?? XLSX_PREVIEW_IMAGE_FALLBACK_SIZE_PX)
  );

  return {
    ...fromLayout,
    height,
    width,
  };
};

const getRowDataByIndex = (rowData: SpreadsheetNoteData['content']['row_data'] | undefined, rowIndex: number) => {
  if (!rowData) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(rowData, rowIndex)) {
    return rowData[rowIndex];
  }

  return rowData[rowIndex + 1];
};

const getMaxImageRowIndex = (images: SpreadsheetNoteXlsxImage[]) =>
  images.reduce((maxRowIndex, image) => Math.max(maxRowIndex, image.from.row, image.to?.row ?? image.from.row), 0);

const XlsxImageOverlay = ({ columnOffset, columnWidths, images, rowHeights, rowOffset }: XlsxImageOverlayProps) => {
  if (!images.length) {
    return null;
  }

  return (
    <Box
      aria-hidden
      sx={{
        inset: 0,
        pointerEvents: 'none',
        position: 'absolute',
      }}>
      {images.map(image => {
        const layout = getImageLayout(image, columnWidths, rowHeights, columnOffset, rowOffset);

        return (
          <Box
            alt={image.alt}
            component="img"
            key={image.id}
            src={image.src}
            sx={{
              height: layout.height,
              left: layout.left,
              objectFit: 'contain',
              position: 'absolute',
              top: layout.top,
              userSelect: 'none',
              width: layout.width,
            }}
          />
        );
      })}
    </Box>
  );
};

const SpreadsheetNote = ({
  note,
  workbook,
  onChange,
  parentContainerHeight,
  isAttributeMapperEnabled,
  cells,
  isXlsxPreview = false,
  xlsxPreviewOptions,
}: SpreadsheetNoteItemProps) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const [measuredPreviewWidth, setMeasuredPreviewWidth] = useState(() =>
    typeof window !== 'undefined' ? Math.min(window.innerWidth, MAX_CONTAINER_WIDTH) : MAX_CONTAINER_WIDTH
  );
  const activeXlsxPreviewOptions = isXlsxPreview ? xlsxPreviewOptions : undefined;
  const shouldUseXlsxViewerEnhancements = Boolean(activeXlsxPreviewOptions);
  const [resizedColumnWidths, setResizedColumnWidths] = useState<Record<string, number>>({});
  const [resizedRowHeights, setResizedRowHeights] = useState<Record<number, number>>({});

  const columnTypes = useMemo(() => note.alphabet ?? [], [note.alphabet]);

  const baseColumnWidthList = useMemo(
    () =>
      columnTypes.map((column, index) => {
        const sourceIndex = isAttributeMapperEnabled ? index - 1 : index;
        const columnWidth =
          sourceIndex >= 0
            ? Object.values(note.initialObj?.content?.column_data ?? {})[sourceIndex]?.column_width
            : undefined;

        return {
          column,
          width: getXlsxColumnWidthPx(columnWidth) ?? DEFAULT_XLSX_PREVIEW_COLUMN_WIDTH_PX,
        };
      }),
    [columnTypes, isAttributeMapperEnabled, note.initialObj?.content?.column_data]
  );

  const columnWidths = useMemo(() => baseColumnWidthList.map(({ width }) => width), [baseColumnWidthList]);

  const resizedColumnWidthList = useMemo(
    () =>
      baseColumnWidthList.map(({ column, width }) => ({
        column,
        width: resizedColumnWidths[column] ?? width,
      })),
    [baseColumnWidthList, resizedColumnWidths]
  );
  const resizedColumnWidthsForVirtualization = useMemo(
    () => resizedColumnWidthList.map(({ width }) => width),
    [resizedColumnWidthList]
  );

  const xlsxPresentationImages = useMemo(
    () => (isXlsxPreview ? (note.initialObj?.xlsxPresentationImages ?? []) : []),
    [isXlsxPreview, note.initialObj?.xlsxPresentationImages]
  );

  const baseRowHeightList = useMemo(() => {
    const rowData = note.initialObj?.content?.row_data;
    const rowCount = Math.max(getXlsxPreviewRowCount(note), getMaxImageRowIndex(xlsxPresentationImages) + 1);
    return Array.from({ length: rowCount }, (_, rowIndex) => ({
      height: getXlsxRowHeightPx(getRowDataByIndex(rowData, rowIndex)?.row_height) ?? DEFAULT_XLSX_ROW_HEIGHT_PX,
      rowNumber: rowIndex + 1,
    }));
  }, [note, xlsxPresentationImages]);

  const resizedRowHeightList = useMemo(() => {
    const rowHeightByNumber = new Map(baseRowHeightList.map(({ height, rowNumber }) => [rowNumber, height]));

    Object.entries(resizedRowHeights).forEach(([rowNumber, height]) => {
      rowHeightByNumber.set(Number(rowNumber), height);
    });

    return Array.from(rowHeightByNumber.entries())
      .sort(([rowNumberA], [rowNumberB]) => rowNumberA - rowNumberB)
      .map(([rowNumber, height]) => ({ height, rowNumber }));
  }, [baseRowHeightList, resizedRowHeights]);

  const rowHeights = useMemo(() => {
    const sourceRowHeights = resizedRowHeightList.map(({ height }) => height);
    return isAttributeMapperEnabled ? [INTERNAL_REFERENCE_XLSX_ROW_HEIGHT_PX, ...sourceRowHeights] : sourceRowHeights;
  }, [isAttributeMapperEnabled, resizedRowHeightList]);

  const handleColumnResize = useCallback(
    (column: string, width: number) => {
      const defaultWidth = baseColumnWidthList.find(columnWidth => columnWidth.column === column)?.width;
      setResizedColumnWidths(currentWidths => {
        const nextWidths = { ...currentWidths };

        if (defaultWidth != null && Math.round(defaultWidth) === Math.round(width)) {
          delete nextWidths[column];
        } else {
          nextWidths[column] = width;
        }

        return nextWidths;
      });
    },
    [baseColumnWidthList]
  );

  const handleRowResize = useCallback(
    (rowNumber: number, height: number) => {
      const defaultHeight = baseRowHeightList.find(rowHeight => rowHeight.rowNumber === rowNumber)?.height;
      setResizedRowHeights(currentHeights => {
        const nextHeights = { ...currentHeights };

        if (defaultHeight != null && Math.round(defaultHeight) === Math.round(height)) {
          delete nextHeights[rowNumber];
        } else {
          nextHeights[rowNumber] = height;
        }

        return nextHeights;
      });
    },
    [baseRowHeightList]
  );

  // Preview virtualization needs the rendered panel width, which is only reliable after layout.
  useLayoutEffect(() => {
    if (!isXlsxPreview) {
      return;
    }
    const el = measureRef.current;
    if (!el) {
      return;
    }
    const applyWidth = (w: number) => {
      if (w > 0) {
        setMeasuredPreviewWidth(Math.floor(w));
      }
    };
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w != null) {
        applyWidth(w);
      }
    });
    ro.observe(el);
    applyWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [isXlsxPreview]);

  const containerWidth = useMemo(() => {
    if (isXlsxPreview) {
      return Math.max(1, Math.min(measuredPreviewWidth, MAX_CONTAINER_WIDTH));
    }
    return Math.min(window.innerWidth, MAX_CONTAINER_WIDTH);
  }, [isXlsxPreview, measuredPreviewWidth]);

  const xlsxPreviewRowCount = useMemo(() => (isXlsxPreview ? getXlsxPreviewRowCount(note) : 0), [isXlsxPreview, note]);

  const useVirtualizationForXlsxPreview = xlsxPreviewRowCount >= XLSX_PREVIEW_VIRTUALIZATION_ROW_THRESHOLD;
  const spreadsheetSheet = note as unknown as ScalarSpreadsheetProps['sheet'];
  const spreadsheetConfigProps = note as unknown as Omit<ScalarSpreadsheetProps, 'onChange' | 'sheet'>;

  const virtualization = useMemo(() => {
    if (isXlsxPreview && !useVirtualizationForXlsxPreview) {
      return undefined;
    }
    return {
      rowHeight: isXlsxPreview ? XLSX_PREVIEW_VIRTUAL_ROW_HEIGHT : 30,
      rowOverscanCount: 6,
      height: parentContainerHeight,
      width: containerWidth,
      columnWidths: isXlsxPreview ? resizedColumnWidthsForVirtualization : columnWidths,
      columnOverscanCount: 4,
    };
  }, [
    isXlsxPreview,
    useVirtualizationForXlsxPreview,
    parentContainerHeight,
    containerWidth,
    columnWidths,
    resizedColumnWidthsForVirtualization,
  ]);

  return (
    <CustomContainer
      data-testid={isXlsxPreview ? 'spreadsheet-note-xlsx-preview' : 'spreadsheet-note'}
      {...(isXlsxPreview ? { 'data-xlsx-wrap-layout': useVirtualizationForXlsxPreview ? 'virtual' : 'natural' } : {})}
      {...(isXlsxPreview
        ? { 'data-xlsx-reference-layout': isAttributeMapperEnabled ? 'attribute-mapper' : 'read-only-preview' }
        : {})}>
      <Box ref={isXlsxPreview ? measureRef : undefined} sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
        {note.cells && cells && (
          <Grid py={3} px={1} size={{ xs: 12 }} sx={{ position: 'relative' }}>
            <ScalarSpreadsheet
              {...spreadsheetConfigProps}
              onChange={onChange}
              sheet={spreadsheetSheet}
              workbook={workbook}
              showToolbar
              virtualization={virtualization}
              disablePerfectScrollbar
              columnTypes={shouldUseXlsxViewerEnhancements ? columnTypes : undefined}
              columnWidths={shouldUseXlsxViewerEnhancements ? resizedColumnWidthList : undefined}
              onColumnResize={activeXlsxPreviewOptions?.allowColumnResize ? handleColumnResize : undefined}
              xlsxPreviewOptions={
                activeXlsxPreviewOptions
                  ? {
                    allowMergedSelectionIndicatorSpan: activeXlsxPreviewOptions.allowMergedSelectionIndicatorSpan,
                    defaultColumnWidths: baseColumnWidthList,
                    defaultRowHeights: baseRowHeightList,
                    disableLegendSelection: activeXlsxPreviewOptions.disableLegendSelection,
                    enableCopyFeedback: activeXlsxPreviewOptions.enableCopyFeedback,
                    frozenPaneColumnCellKey: activeXlsxPreviewOptions.frozenPaneColumnCellKey,
                    frozenPaneRowCellKey: activeXlsxPreviewOptions.frozenPaneRowCellKey,
                    onToggleFrozenColumnsAtCell: activeXlsxPreviewOptions.onToggleFrozenColumnsAtCell,
                    onToggleFrozenRowsAtCell: activeXlsxPreviewOptions.onToggleFrozenRowsAtCell,
                    onRowResize: activeXlsxPreviewOptions.allowRowResize ? handleRowResize : undefined,
                    reserveColumnIndicatorSpace: activeXlsxPreviewOptions.reserveColumnIndicatorSpace,
                    resizeHandlesInColumnLegend: activeXlsxPreviewOptions.resizeHandlesInColumnLegend,
                    rowHeights: resizedRowHeightList,
                  }
                  : undefined
              }
            />
            {isXlsxPreview && !useVirtualizationForXlsxPreview && (
              <XlsxImageOverlay
                columnOffset={isAttributeMapperEnabled ? 1 : 0}
                columnWidths={resizedColumnWidthsForVirtualization}
                images={xlsxPresentationImages}
                rowHeights={rowHeights}
                rowOffset={isAttributeMapperEnabled ? 1 : 0}
              />
            )}
          </Grid>
        )}
      </Box>
    </CustomContainer>
  );
};

export default SpreadsheetNote;
